import React from "react";
import {VOLLEYBALL_PERIODS} from "../constants/volleyballPeriods";

/**
 * Get the score for a specific team in the current set
 *
 * @param team 'home' | 'away'
 * @param currentSet The current set identifier (e.g., 'S1', 'S2', etc.)
 * @param game The game object containing period scores
 * @returns {number|*|number} The score for the specified team in the current set
 */
const getGameScore = (team, currentSet, game) => {
  switch (currentSet) {
    case VOLLEYBALL_PERIODS.S1:
      return game?.periods?.first?.[team] || 0;
    case VOLLEYBALL_PERIODS.S2:
      return game?.periods?.second?.[team] || 0;
    case VOLLEYBALL_PERIODS.S3:
      return game?.periods?.third?.[team] || 0;
    case VOLLEYBALL_PERIODS.S4:
      return game?.periods?.fourth?.[team] || 0;
    case VOLLEYBALL_PERIODS.S5:
      return game?.periods?.fifth?.[team] || 0;
    default:
      return 0;
  }
}

const getLastSet = (game) => {
  const isSet = /S\d/.test(game?.status?.short);
  let lastSet = isSet ? game.status.short : 'S1'

  if (!isSet) {
    const homeScore = game?.scores?.home ? game.scores.home : 0
    const awayScore = game?.scores?.away ?game.scores.away : 0
    const set = homeScore > awayScore ? homeScore : awayScore

    lastSet = `S${set}`
  }

  return lastSet
}

export const DesktopGameCard = ({ game , isSelected, onGameSelect }) => {
  const lastSet = getLastSet(game)
  const homeSetScore = getGameScore('home', lastSet, game);
  const awaySetScore = getGameScore('away', lastSet, game);

  const hasGameScore = typeof game?.scores?.home === "number" && typeof game?.scores?.away === "number";
  const shouldShowSetScore = hasGameScore && game?.status?.short && game.status.short !== 'NS';
  const setScores = hasGameScore
    ? { home: game.scores.home, away: game.scores.away }
    : null;
  const pointsToDisplay = shouldShowSetScore
    ? { home: homeSetScore, away: awaySetScore }
    : null
  const teams = [
    { key: 'home', fallback: 'Home' },
    { key: 'away', fallback: 'Away' },
  ];

  const cardClasses = `relative border ${isSelected ? 'border-white/60 shadow-[0_0_20px_rgba(255,255,255,0.05)]' : 'border-white/10 hover:border-white/40'} rounded-sm p-5 bg-white/[0.03] transition-all duration-200 cursor-pointer`;

  const statusLabel = game.status?.long || 'Not Started';

  return (
    <div className={cardClasses} onClick={() => onGameSelect(game)}>
      <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-500">
        <div className="flex items-center gap-2">
          <span>{game.country?.code || game.country?.name || 'N/A'}</span>
          {!!game.country?.flag && (
            <img
              src={game.country.flag}
              alt={game.country.name}
              className="h-3 w-5 object-cover rounded-sm"
              loading="lazy"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span>{game.league?.name || 'League'}</span>
          {!!game.league?.logo && (
            <img
              src={game.league.logo}
              alt={game.league.name}
              className="h-4 w-4 object-contain"
              loading="lazy"
            />
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center text-[11px] font-mono uppercase tracking-widest text-gray-500">
        <span>{statusLabel}</span>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="space-y-3 flex-1">
          {teams.map(({ key, fallback }, index) => {
            const team = game?.teams?.[key];
            return (
              <div key={key} className={`flex items-center gap-3 ${index === 0 ? 'pt-4' : ''}`}>
                {!!team?.logo && (
                  <img
                    src={team.logo}
                    alt={team.name}
                    className="w-10 h-10 object-contain border border-white/10 rounded-sm bg-black/40"
                    loading="lazy"
                  />
                )}
                <div>
                  <p className="text-sm font-mono text-white tracking-tight leading-tight">
                    {team?.name || fallback}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-stretch gap-6 ml-4">
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Pts</p>
            {teams.map(({ key }, index) => (
              <p key={`${key}-points`} className={`text-2xl font-black text-white tabular-nums leading-tight ${index !== 0 ? 'mt-5' : ''}`}>
                {pointsToDisplay ? pointsToDisplay[key] : '--'}
              </p>
            ))}
          </div>
          <span className="w-px bg-white/10" />
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Set</p>
            {teams.map(({ key }, index) => (
              <p key={`${key}-set`} className={`text-xl font-black text-white tabular-nums leading-tight ${index !== 0 ? 'mt-6' : ''}`}>
                {setScores ? setScores[key] : '--'}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DesktopGameCard;
